import type {
  ActiveSession,
  ChangePasswordPayload,
  NotificationPreferences,
  PersonalizationPreferences,
  PersonalizationUpdate,
  TrustedDevice,
  UpdateProfilePayload,
  UserProfile,
} from "@/types/settings";
import { apiClient } from "./client";

export const settingsApi = {
  // Profile
  getProfile() {
    return apiClient.get<UserProfile>("/settings/profile");
  },
  updateProfile(payload: UpdateProfilePayload) {
    return apiClient.put<UserProfile>("/settings/profile", payload);
  },
  uploadAvatar(formData: FormData) {
    return apiClient.postFormData<{ avatarUrl: string }>(
      "/settings/profile/avatar",
      formData,
    );
  },
  deleteAvatar() {
    return apiClient.delete<void>("/settings/profile/avatar");
  },

  // Security
  changePassword(payload: ChangePasswordPayload) {
    return apiClient.post<void>("/settings/security/password", payload);
  },
  getSessions() {
    return apiClient.get<ActiveSession[]>("/settings/security/sessions");
  },
  revokeSession(sessionId: string) {
    return apiClient.delete<void>(`/settings/security/sessions/${sessionId}`);
  },
  revokeOtherSessions() {
    return apiClient.post<void>("/settings/security/sessions/revoke-others");
  },

  getTrustedDevices(): Promise<TrustedDevice[]> {
    return apiClient.get<TrustedDevice[]>("/settings/security/trusted-devices");
  },
  removeTrustedDevice(deviceId: string): Promise<void> {
    return apiClient.delete<void>(
      `/settings/security/trusted-devices/${deviceId}`,
    );
  },

  // Notifications
  getNotificationPreferences() {
    return apiClient.get<NotificationPreferences>("/settings/notifications");
  },
  updateNotificationPreferences(payload: Partial<NotificationPreferences>) {
    return apiClient.put<NotificationPreferences>(
      "/settings/notifications",
      payload,
    );
  },

  // Personalization
  getPersonalization() {
    return apiClient.get<PersonalizationPreferences>(
      "/settings/personalization",
    );
  },
  updatePersonalization(payload: PersonalizationUpdate & { theme: string }) {
    return apiClient.put<PersonalizationPreferences>(
      "/settings/personalization",
      payload,
    );
  },

  // Privacy
  downloadDataExport() {
    return apiClient.getBlob("/settings/privacy/export");
  },
  deleteAccount(password: string) {
    return apiClient.post<void>("/settings/privacy/delete-account", {
      password,
    });
  },
};
